import { createContext, useState, useEffect, useContext } from "react";
import useLogin from "../hooks/useLogin";
import { UserData } from "../types";
import { useInactivityLogout } from "../hooks/useInactivityLogout";
import { INACTIVITY_LOGOUT_TIME, STORAGE_KEY_AUTH, STORAGE_KEY_LAST_ACTIVITY } from "../constants";

type AuthContextType = {
	isAuthenticated: boolean;
	userData?: UserData;
	login: (username: string, password: string) => Promise<boolean>;
	logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({
	children,
	setLocale,
}: {
	children: React.ReactNode;
	setLocale: (locale: "en" | "de" | "fr") => void;
}) => {
	const loginMutation = useLogin();

	const [userData, setUserData] = useState<UserData | undefined>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_AUTH);
		return stored ? JSON.parse(stored) : undefined;
	});

	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
		return !!localStorage.getItem(STORAGE_KEY_AUTH);
	});

	useInactivityLogout(() => {
		setIsAuthenticated(false);
		localStorage.removeItem(STORAGE_KEY_AUTH);
	}, INACTIVITY_LOGOUT_TIME);

	useEffect(() => {
		const userDataStorage = localStorage.getItem(STORAGE_KEY_AUTH);

		if (isAuthenticated && userDataStorage) {
			const _userData = JSON.parse(userDataStorage);
			setUserData(_userData);
			setLocale((_userData.user.locale?.split("-")[0] || "en") as "en" | "de" | "fr");
		} else {
			setUserData(undefined);
		}
	}, [isAuthenticated]);

	const login = async (username: string, password: string) => {
		try {
			const response = await loginMutation.mutateAsync({ username, password });

			if (response?.token) {
				setIsAuthenticated(true);
				localStorage.setItem(STORAGE_KEY_AUTH, JSON.stringify(response));

				// ✅ Reset lastActivity on login. Important for inactivity logout hook.
				localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, Date.now().toString());

				return true;
			}
		} catch (error) {
			console.error("Login failed:", error);
		}

		return false;
	};

	const logout = () => {
		setIsAuthenticated(false);
		localStorage.removeItem(STORAGE_KEY_AUTH);
	};

	return <AuthContext.Provider value={{ isAuthenticated, userData, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext)!;
